// Presentation helpers for the audit surfaces (PB-1588 domain 1).
//
// Audit rows are written by every service for machines to read: actions arrive
// as `user_login_completed` or `mail.sender.activated`, entities as a type plus
// a bare UUID, and payloads as free-form JSON. Rendered verbatim that is a wall
// of identifiers an administrator has to decode.
//
// The rule these helpers follow: nothing machine-readable reaches the reader.
// This started out showing the exact value beside the readable one, on the
// argument that an auditor needs to quote the literal `user_login_completed`.
// That was wrong about the audience — the reader here is an administrator, not
// an engineer, and an identifier on screen reads as an unfinished internal
// tool. Actions, entity types, writing services and payload VALUES are all
// mapped; an id is withheld rather than abbreviated.

import { PRODUCT_NAME } from "@playbookai/types";

/**
 * Curated copy for actions where the mechanical rendering is misleading or
 * needlessly long. Anything absent falls back to `sentenceCase`, which is
 * right often enough — so a service adding an action tomorrow renders
 * sensibly without a change here, rather than rendering blank.
 *
 * Derived from the actions actually present in `audit.audit_log`, not
 * invented: auth, notification and playbook-be all write here.
 */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  "solve.access_restored": "Access restored",
  "vsm.access_restored": "Access restored",
  "lama.access_restored": "Access restored",
  user_login_completed: "Signed in",
  /* PB-5461: a sign-in found the person's link under an older sign-in
     address and moved it to the current one. */
  keycloak_identity_issuer_updated: "Sign-in address updated",
  logout: "Signed out",
  session_revoked: "Session revoked",
  membership_granted: "Added to a Solve",
  membership_revoked: "Removed from a Solve",
  tenant_role_changed: "Workspace role changed",
  local_user_provisioned: "Account created",
  local_user_provisioning_started: "Account creation started",
  local_password_reset_operation_opened: "Password reset operation started",
  local_password_reset_operation_interrupted:
    "Password reset operation interrupted",
  local_password_reset_operation_sign_out_recorded:
    "Password reset sign-out recorded",
  local_user_password_changed: "Password changed",
  directory_user_provisioned: "Added from the directory",
  directory_user_provisioning_replayed: "Directory provisioning replayed",
  directory_group_role_mapping_created: "Group role rule created",
  deployment_bootstrap_applied: "Deployment bootstrap applied",
  tenant_configuration_updated: "Workspace configuration changed",
  tenant_profile_updated: "Workspace profile changed",
  tenant_module_enablement_changed: "Module turned on or off",
  tenant_module_enabled: "Module turned on",
  tenant_module_disabled: "Module turned off",
  tenant_identity_provider_status_recorded: "Identity provider status recorded",
  tenant_player_ai_chat_recovery_updated:
    "Backup connection for Player AI changed",
  widget_created: "Widget created",
  widget_updated: "Widget updated",
  notification_marked_read: "Notification marked read",
  notifications_marked_read: "Notifications marked read",
  notifications_marked_seen: "Notifications marked seen",

  /* Identity & directory operational events (PB-5332). The Identity Activity view
     classifies these by source; the WORDING lives here so both screens read alike. */
  directory_sync_started: "Directory sync started",
  directory_sync_completed: "Directory sync completed",
  directory_sync_failed: "Directory sync failed",
  directory_sync_abandoned: "Directory sync abandoned",
  directory_sync_reclaimed: "Directory sync reclaimed",
  directory_sync_abandoned_run_reclaimed: "Abandoned directory sync reclaimed",
  directory_sync_directory_enabled: "Directory sync turned on",
  directory_sync_directory_disabled: "Directory sync turned off",
  directory_sync_groups_changed: "Directory groups changed",
  directory_sync_profile_updated: "Profile updated from the directory",
  directory_sync_manager_linked: "Reporting line linked",
  directory_sync_manager_cycle_rejected: "Reporting line rejected as circular",
  directory_sync_user_deactivated: "Deactivated in the directory",
  directory_sync_user_reactivated: "Reactivated in the directory",
  directory_sync_reactivation_ignored: "Directory reactivation ignored",
  directory_profile_refreshed: "Profile refreshed",
  directory_user_unavailable: "No longer present in the directory",
  directory_group_created: "Directory group created",
  directory_group_member_added: "Added to a directory group",
  directory_group_member_removed: "Removed from a directory group",
  directory_group_role_mapping_replaced: "Group role rule replaced",
  directory_group_role_mapping_retired: "Group role rule retired",
  scim_user_provisioned: "Added by your identity provider",
  scim_user_updated: "Updated by your identity provider",
  scim_user_deactivated: "Deactivated by your identity provider",
  scim_user_reactivated: "Reactivated by your identity provider",
  scim_user_reactivation_ignored: "Reactivation ignored",
  scim_user_update_ignored: "Update ignored",
  scim_group_created: "Directory group created by your identity provider",
  scim_group_renamed: "Directory group renamed",
  scim_group_archived: "Directory group archived",
  scim_group_members_changed: "Directory group members changed",
  scim_group_scope_required: "Directory group sync needs the groups permission",
  scim_oauth_client_registered: "Provisioning credential created",
  scim_oauth_client_revoked: "Provisioning credential revoked",
  scim_oauth_client_secret_rotated: "Provisioning secret rotated",
  scim_oauth_previous_secret_expired: "Previous provisioning secret expired",
  scim_oauth_no_previous_secret: "No previous provisioning secret to expire",
  scim_oauth_token_issued: "Provisioning credential used",
  scim_oauth_client_limit_reached: "Provisioning credential limit reached",
  scim_oauth_client_not_active: "Provisioning credential rejected as inactive",
  scim_oauth_client_not_found: "Provisioning credential not recognised",
  scim_oauth_client_digest_upgraded: "Provisioning secret storage upgraded",
  identity_projection_requeued: "Access change queued again",
  local_user_provisioning_cancelled: "Account creation cancelled",
  local_user_provisioning_recovery_required: "Account creation needs attention",
  tenant_invite_created: "Invitation sent",
  tenant_invite_accepted: "Invitation accepted",
  tenant_invite_revoked: "Invitation revoked",
  tenant_membership_status_changed: "Workspace access changed",

  /* Shared Teams (PB-5332 D17). A Team grants no access; these say who is in one,
     which is what a LAMA campaign expands. */
  team_created: "Team created",
  team_updated: "Team renamed or described",
  team_archived: "Team archived",
  team_archived_by_admin: "Team archived by an admin",
  team_ownership_recovered: "Team ownership recovered",
  team_invitation_created: "Team invitation sent",
  team_invitation_accepted: "Team invitation accepted",
  team_invitation_declined: "Team invitation declined",
  team_invitation_withdrawn: "Team invitation withdrawn",
  team_join_requested: "Request to join a team",
  team_join_request_approved: "Request to join a team approved",
  team_join_request_declined: "Request to join a team declined",
  team_join_request_cancelled: "Request to join a team cancelled",
  team_member_role_changed: "Team role changed",
  team_member_removed: "Removed from a team",
  team_member_left: "Left a team",

  /* Integration sign-in (PB-5331). "Integration sign-in" rather than a bare
     "Sign-in": `user_login_completed` above already reads "Signed in", and in a
     mixed feed the two would be read as the same kind of event. A cancellation
     reads identically whichever surface it came from — the console's Cancel
     button or the provider's consent screen — because both now write one
     action; the row's `origin` is what says which. */
  authorization_started: "Integration sign-in started",
  authorization_processing: "Integration sign-in in progress",
  authorization_cancelled: "Integration sign-in cancelled",
  authorization_failed: "Integration sign-in failed",
  authorization_attempt_expired: "Integration sign-in expired",
};

/** The action keys this map covers.
 *
 * Exported ONLY so a test can assert it equals the server's set. The two maps
 * are deliberately separate — user-facing copy does not belong in a published
 * contract — but a key present on one side and missing on the other means the
 * screen and the CSV disagree about the SAME row, which is the drift this
 * exists to catch. It had already happened once. */
export const ACTION_LABEL_KEYS: readonly string[] = Object.keys(ACTION_LABELS);

/**
 * `some_thing.here` / `some_thing_here` / `someThingHere` -> `Some thing here`.
 *
 * camelCase is split as well as `._-`. Without it every camelCase name reached
 * the reader as a run-together identifier — `secretUri` rendered "SecretUri",
 * `fromAddress` rendered "FromAddress" — which is exactly the "identifier on
 * screen" this file exists to prevent. Audit payloads are full of them: 108
 * distinct camelCase keys across 52% of the rows of a real 13.9k-row log, plus
 * Hoshin's `submitForReview` / `returnToActive` actions.
 *
 * The boundary is lowercase-or-digit followed by uppercase, and only the
 * split-off letter is lowered — so an all-caps acronym survives (`VSM` stays
 * `VSM`, not `Vsm`) and an already-spaced value is untouched.
 */
export function sentenceCase(raw: string): string {
  const words = raw
    .replace(/[._-]+/g, " ")
    // camelCase too. Lowercasing ONLY the split-off letter, never the whole
    // string: a blanket `.toLowerCase()` turns an acronym into "Vsm".
    .replace(
      /([a-z0-9])([A-Z])/g,
      (_match, before, after) => `${before} ${after.toLowerCase()}`,
    )
    .trim();
  if (!words) return raw;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Readable label for an audit action. Never empty: falls back to the
    mechanical rendering so an unknown action is still legible. */
/* `Object.hasOwn`, not a bare index. `LABELS["constructor"]` resolves up the
   prototype chain to a FUNCTION, which `??` does not treat as missing — so an
   action named `constructor`, `toString` or `hasOwnProperty` would render as
   JavaScript internals instead of falling back to the readable rendering.
   Audit actions are written by every service, so the key space is not ours. */
export function actionLabel(action: string): string {
  return Object.hasOwn(ACTION_LABELS, action)
    ? (ACTION_LABELS[action] as string)
    : sentenceCase(action);
}

/** Which part of the product wrote the row.
 *
 * Deployment names like `playbook-be` describe how the system is built, not
 * what happened. An unmapped service still renders as words, so a new one
 * never lands on screen as a bare slug. The product modules are listed
 * explicitly because the fallback would render them "Kpi" and "Vsm" —
 * acronyms it cannot know about. Taken from the DISTINCT services actually
 * present in `audit.audit_log`, not guessed. Mirrors SERVICE_LABELS in the auth
 * service's audit-wording.ts — duplicated for the same reason the rest of this
 * file is: user-facing copy does not belong in a published contract. */
const SERVICE_LABELS: Readonly<Record<string, string>> = {
  auth: "Sign-in & identity",
  "playbook-be": `${PRODUCT_NAME} modules`,
  notification: "Notifications",
  /* Distinct labels because these are distinct services. Sharing
     "Notifications" put two adjacent, identical entries in the Source service
     picker, which a reader cannot tell apart and whose filter result they
     cannot predict from the label (PB-5243). `notification` holds what a
     tenant configured and acknowledged; `notification-outbox` is the delivery
     pipeline that leases, sends, dead-letters and requeues an intent. */
  "notification-outbox": "Notification delivery",
  collab: "Collaboration",
  ais: "AI service",
  "player-ai": "Player AI",
  document: "Documents",
  solve: "SOLVE",
  vsm: "VSM",
  hoshin: "Hoshin",
  kpi: "KPI Tracker",
  lama: "LAMA",
  widget: "Widgets",
  "work-links": "Work links",
};

/** Exported only so a test can assert parity with the other copy. */
export const SERVICE_LABEL_KEYS: readonly string[] =
  Object.keys(SERVICE_LABELS);

export function serviceLabel(service: string): string {
  return Object.hasOwn(SERVICE_LABELS, service)
    ? (SERVICE_LABELS[service] as string)
    : sentenceCase(service);
}

/** `solve_membership` -> `Solve membership`. */
export function entityTypeLabel(entityType: string): string {
  return sentenceCase(entityType);
}

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/* THE rule for identifiers on the audit surfaces: never the raw value, never
   deleted, always a stable reference. One rule, after two attempts that failed
   in opposite directions — showing the raw value beside the readable one, then
   deleting it, which left two rows about genuinely different widgets
   indistinguishable.

   64 bits, not 40. The first version folded two correlated 32-bit FNV passes
   into 40 bits and claimed "effectively never collide" on the strength of a
   probe over `id-0`…`id-199999` — a narrow input set presented as a general
   property, which it was not. A reviewer's 3,000,000-input probe found
   `id-1123976` and `id-1868875` colliding at `10M5-6TPZ`, and at 40 bits the
   birthday probability over 200k identifiers is ~1.8% regardless. A collision
   recreates the exact defect the reference exists to remove, so the margin has
   to be real: at 64 bits, a million entities collide with probability ~3e-8.

   `entity_id` is TEXT and this formatter accepts arbitrary strings, so the
   input domain is unbounded — plain FNV-1a 64 over the UTF-16 code units,
   which is a single well-understood pass rather than a hand-rolled fold.
   Rendered in Crockford base32, which drops I/L/O/U so a reference cannot be
   misread from a screenshot or over the phone.

   Duplicated verbatim in the other formatter and pinned by a parity test: the
   screen and the CSV must produce the SAME reference for a row, or an
   administrator comparing them sees two different things. */
const CROCKFORD_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const FNV64_OFFSET = 0xcbf29ce484222325n;
const FNV64_PRIME = 0x100000001b3n;
const U64 = 0xffffffffffffffffn;

export function entityReference(identifier: string): string {
  let hash = FNV64_OFFSET;
  for (let i = 0; i < identifier.length; i++) {
    hash = (hash ^ BigInt(identifier.charCodeAt(i))) & U64;
    hash = (hash * FNV64_PRIME) & U64;
  }
  let out = "";
  for (let i = 0; i < 13; i++) {
    out = CROCKFORD_ALPHABET[Number((hash >> BigInt(i * 5)) & 31n)] + out;
  }
  return `${out.slice(0, 4)}-${out.slice(4, 8)}-${out.slice(8)}`;
}

export interface PayloadEntry {
  /** The RAW payload key, carried for identity rather than display.
   *
   * `key` below is humanised, and `sentenceCase` is many-to-one: `displayName`
   * and `display_name` both render "Display name", as do `a.b` and `a_b`. A
   * consumer keying a list on the label alone therefore emits duplicate React
   * keys for a payload holding two such fields. */
  readonly field: string;
  readonly key: string;
  readonly value: string;
}

/** A value written as a machine token: `deployment_bootstrap`, `no_change`,
    `microsoft_entra`.
 *
 * UNDERSCORES only, deliberately. Dots and hyphens appear in real user data —
 * `acme.test` is a domain, `1.2.3` a version, `north-west` a team name — and
 * humanising those corrupts the record rather than clarifying it. An
 * underscore-separated lowercase token is not something a person types. */
const MACHINE_TOKEN_RE = /^[a-z0-9]+(_[a-z0-9]+)+$/;

/** The same shape written in SCREAMING_SNAKE_CASE: `WORKFLOW_EXECUTION_FAILED`.
 *
 * Separate from `MACHINE_TOKEN_RE` because the two need different handling,
 * not because the shape differs. `sentenceCase` lowers only the letter it
 * splits off, deliberately, so that an acronym survives it — which means an
 * all-caps token passes through unchanged and still reaches the reader
 * shouting. Lowering it first is what turns it into prose.
 *
 * Underscore required, exactly as above. A bare `VSM`, `NONE` or `US` is an
 * acronym or a word rather than a machine token, and lowering those would
 * corrupt the record instead of clarifying it.
 *
 * KNOWN RESIDUAL: a multi-segment token that contains an acronym is lowered
 * with the rest of it, so `KPI_TRACKER_ENABLED` reads "Kpi tracker enabled".
 * Still a clear improvement on the raw passthrough it replaces, and no such
 * value is written to audit payloads today. Sparing an embedded acronym needs a
 * curated list, which is a bigger contract than this rule — and it would have to
 * be kept identical in both copies, so it belongs behind the parity gate's
 * vectors if it is ever added.
 */
const SCREAMING_TOKEN_RE = /^[A-Z0-9]+(_[A-Z0-9]+)+$/;

/** Render one payload value as prose.
 *
 * `null` is "None", not the string "null"; booleans are Yes/No, not "true";
 * an ISO timestamp becomes a local date.
 *
 * The KEY side of a payload was humanised from the start and the VALUE side
 * was not, which left the Detail column as the one place raw internals still
 * reached the screen — real writers record `Source: deployment_bootstrap`,
 * `Reason: membership_suspended_by_admin`, and bare UUIDs. Values now get the
 * same treatment: an identifier is withheld rather than shown, and a machine
 * token is read as the sentence it plainly is.
 *
 * Structured values flatten into prose instead of JSON. At depth the shape
 * does carry information, but `{"identityProvider":"keycloak_local"}` in a
 * table cell is not something to hand an administrator. */
/* Values are RENDERED readably, never dropped. An earlier pass replaced any
   UUID with the literal "Not shown" and blanked the affected cell for every
   entity type the resolver did not cover — which deleted the one value that
   told two otherwise-identical rows apart, in the artefact whose whole purpose
   is telling them apart. The rule was always about the FORM a value takes, not
   about removing it. */
/* `depth` bounds the recursion below. The payload is service-written, so a
   deep or cyclic one is not the expected case — but it CONTAINS user-authored
   values (a Solve title, a workspace name), it is rendered into both a table
   cell and a CSV field, and nothing between the writer and here caps it. A
   bound costs one parameter; the alternative is a page that hangs on a row
   nobody can delete, in an append-only table. */
const MAX_VALUE_DEPTH = 6;

/* `timeZone` is a plain defaulted parameter, and this file imports nothing.
   Both are deliberate: `check:audit-label-parity` evaluates these functions as
   PLAIN ES to compare them with the auth service's copy, so it strips a short
   list of type annotations and can neither parse `?:` nor resolve an import.
   An empty zone means "the host's", which is what every caller outside the
   Event details drawer wants — and it keeps this copy's output identical to
   the auth one for the gate's own vectors. */
function formatValue(value: unknown, depth = 0, timeZone = ""): string {
  if (value === null || value === undefined) return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (value === "") return "Empty";
    if (ISO_TIMESTAMP_RE.test(value)) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        /* In the zone the surface renders in, when the caller names one
           (PB-5216 d7): a payload timestamp in the HOST's zone, one row under
           a "When" that names the reader's, is two clocks in one panel with
           nothing saying which is which. The options match `formatDateTime`,
           which is how the rest of the shell renders an instant; an unknown
           zone falls back rather than taking the panel down. */
        if (timeZone) {
          try {
            return new Intl.DateTimeFormat(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
              timeZone,
            }).format(parsed);
          } catch {
            return parsed.toLocaleString();
          }
        }
        return parsed.toLocaleString();
      }
    }
    if (MACHINE_TOKEN_RE.test(value)) return sentenceCase(value);
    // Lowered before sentence-casing — see SCREAMING_TOKEN_RE above for why.
    if (SCREAMING_TOKEN_RE.test(value)) {
      return sentenceCase(value.toLowerCase());
    }
    // An identifier inside a payload gets the same treatment as the entity's
    // own: rendered as a reference, so it is neither shown raw nor lost.
    if (UUID_RE.test(value)) return entityReference(value);
    return value;
  }
  /* Said rather than truncated silently: a cell that just stops is
     indistinguishable from a value that was that short, and this is a
     compliance surface. */
  if (depth >= MAX_VALUE_DEPTH) return "Nested detail not shown";
  if (Array.isArray(value)) {
    return value.length === 0
      ? "None"
      : value
          .map((nested) => formatValue(nested, depth + 1, timeZone))
          .join(", ");
  }
  if (typeof value === "object") {
    const parts = Object.entries(value as Record<string, unknown>).map(
      ([key, nested]) =>
        `${sentenceCase(key)}: ${formatValue(nested, depth + 1, timeZone)}`,
    );
    return parts.length === 0 ? "None" : parts.join("; ");
  }
  return String(value);
}

const ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

/** A `{from, to}` pair, which is how services record a change. */
function isTransition(
  value: unknown,
): value is { from?: unknown; to?: unknown } {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const keys = Object.keys(value);
  return (
    keys.length > 0 &&
    keys.length <= 2 &&
    keys.every((key) => key === "from" || key === "to")
  );
}

/**
 * Describe a `{from, to}` change as a sentence.
 *
 * Services record changes as `{"from":"disabled","to":"enabled"}`. Rendered
 * verbatim that is punctuation an administrator has to parse; rendered as
 * "disabled → enabled" it is the same fact, read at a glance. Setting a value
 * that had none is not a change from "null", it is simply being set — saying
 * "from None" invents a prior state that never existed.
 */
function describeTransition(
  change: { from?: unknown; to?: unknown },
  timeZone = "",
): string {
  const show = (value: unknown) => formatValue(value, 0, timeZone);
  const hadFrom =
    "from" in change && change.from !== null && change.from !== undefined;
  const hasTo = "to" in change && change.to !== null && change.to !== undefined;
  if (hadFrom && hasTo) {
    if (show(change.from) === show(change.to)) {
      return `${show(change.to)} (unchanged)`;
    }
    return `${show(change.from)} → ${show(change.to)}`;
  }
  if (hasTo) return `Set to ${show(change.to)}`;
  if (hadFrom) return `Cleared (was ${show(change.from)})`;
  return "None";
}

/**
 * Flatten an audit payload into label/value pairs for display.
 *
 * Payloads are free-form per writer, so there is no schema to lay out — but
 * raw JSON in a table cell is unreadable, and most of what services record is
 * either a plain value or a `{from, to}` change that has an obvious sentence.
 *
 * Returns an empty array for an empty payload, so callers render a dash rather
 * than an empty container.
 */
export function payloadEntries(
  payload: unknown,
  /** Zone for timestamps inside the payload. Empty means the host's. */
  timeZone = "",
): PayloadEntry[] {
  if (payload === null || payload === undefined) return [];
  if (typeof payload !== "object") {
    return [
      {
        field: "Detail",
        key: "Detail",
        value: formatValue(payload, 0, timeZone),
      },
    ];
  }
  if (Array.isArray(payload)) {
    return payload.length === 0
      ? []
      : [
          {
            field: "Items",
            key: "Items",
            /* Explicit arrow: `map` also passes the index, which `formatValue`
               would read as `depth` — so element 6 onward would render as
               "Nested detail not shown" on a top-level array. */
            value: payload
              .map((item) => formatValue(item, 0, timeZone))
              .join(", "),
          },
        ];
  }
  return Object.entries(payload as Record<string, unknown>).map(
    ([key, value]) => ({
      field: key,
      /* `sentenceCase` alone. It already splits camelCase and lowers ONLY the
         split-off letter, so it produces "Display name" without help — the
         extra blanket `.toLowerCase()` was redundant for every ordinary key
         and destructive for an acronym, rendering `VSM` as "Vsm" at the top
         level while the SAME key nested one deeper went through
         `sentenceCase` directly and kept "VSM". One payload, two spellings.
         (A trailing multi-letter acronym still reads oddly — `tenantURI`
         gives "Tenant uRI" — but that is `sentenceCase`'s own boundary rule
         and the nested path has always rendered it that way.) */
      key: sentenceCase(key),
      value: isTransition(value)
        ? describeTransition(value, timeZone)
        : formatValue(value, 0, timeZone),
    }),
  );
}
